import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Globe, Mail, Lock } from "lucide-react";
import { Reveal, ChapterLabel } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", company: "", email: "", message: "" };

export const ContactSection = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Inquiry received. We will be in touch shortly — confidentially.");
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong. Please try again or reach out directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer id="contact" data-testid="contact-section" className="navy-texture relative bg-[#0B1B3D] text-white">
      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-24 sm:py-32">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          <div>
            <Reveal>
              <ChapterLabel number="04" title="Reach Out" light />
              <h2 className="font-heading mt-8 text-4xl sm:text-5xl font-black tracking-tight text-white">
                Contact Us
              </h2>
              <p className="font-serif-accent italic font-light mt-5 text-white/70 text-base md:text-lg">
                Every conversation begins and stays confidential.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-12 space-y-7">
                <a
                  href="tel:3214741855"
                  data-testid="contact-phone"
                  className="group flex items-center gap-5"
                >
                  <span className="inline-flex items-center justify-center w-11 h-11 border border-white/25 text-[#C5A059]">
                    <Phone size={17} strokeWidth={1.75} />
                  </span>
                  <span>
                    <span className="block text-[10px] font-semibold tracking-[0.28em] uppercase text-white/50">
                      Confidential Line
                    </span>
                    <span className="font-heading text-xl sm:text-2xl font-bold tracking-tight group-hover:text-[#C5A059] transition-colors duration-300">
                      321-474-1855
                    </span>
                  </span>
                </a>

                <a
                  href="https://dpmachininginc.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-website"
                  className="group flex items-center gap-5"
                >
                  <span className="inline-flex items-center justify-center w-11 h-11 border border-white/25 text-[#C5A059]">
                    <Globe size={17} strokeWidth={1.75} />
                  </span>
                  <span>
                    <span className="block text-[10px] font-semibold tracking-[0.28em] uppercase text-white/50">
                      Website
                    </span>
                    <span className="text-base group-hover:text-[#C5A059] transition-colors duration-300">
                      dpmachininginc.com
                    </span>
                  </span>
                </a>

                <div data-testid="contact-emails" className="flex items-start gap-5">
                  <span className="inline-flex items-center justify-center w-11 h-11 border border-white/25 text-[#C5A059] shrink-0">
                    <Mail size={17} strokeWidth={1.75} />
                  </span>
                  <span>
                    <span className="block text-[10px] font-semibold tracking-[0.28em] uppercase text-white/50">
                      Email
                    </span>
                    <a href="mailto:jmack@gwcmgt.com" className="block text-base hover:text-[#C5A059] transition-colors duration-300">
                      jmack@gwcmgt.com
                    </a>
                    <a href="mailto:tmack@gwcmgt.com" className="block text-base hover:text-[#C5A059] transition-colors duration-300">
                      tmack@gwcmgt.com
                    </a>
                  </span>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <form onSubmit={submit} data-testid="inquiry-form" className="border border-white/15 p-8 sm:p-10">
              <p className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.28em] uppercase text-[#C5A059]">
                <Lock size={11} strokeWidth={2.5} />
                Confidential Inquiry
              </p>
              <div className="mt-8 grid sm:grid-cols-2 gap-5">
                <input
                  data-testid="inquiry-name-input"
                  className="dpm-input"
                  placeholder="Name"
                  value={form.name}
                  onChange={set("name")}
                  required
                />
                <input
                  data-testid="inquiry-company-input"
                  className="dpm-input"
                  placeholder="Company"
                  value={form.company}
                  onChange={set("company")}
                  required
                />
              </div>
              <input
                data-testid="inquiry-email-input"
                type="email"
                className="dpm-input mt-5"
                placeholder="Email"
                value={form.email}
                onChange={set("email")}
                required
              />
              <textarea
                data-testid="inquiry-message-input"
                className="dpm-input mt-5 min-h-[130px] resize-y"
                placeholder="Message"
                value={form.message}
                onChange={set("message")}
                required
              />
              <button
                type="submit"
                data-testid="inquiry-submit-button"
                className="dpm-btn-gold mt-8 w-full justify-center"
                disabled={submitting}
              >
                {submitting ? "Sending…" : "Send Confidential Inquiry"}
              </button>
            </form>
          </Reveal>
        </div>

        <div className="relative mt-20 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] tracking-[0.18em] uppercase text-white/40">
          <span data-testid="footer-brand">Defense Precision Machining</span>
          <span>Confidential M&amp;A Outreach — {new Date().getFullYear()}</span>
        </div>
      </div>
    </footer>
  );
};
